import getCurrencyOfferByPeriod from "../../../landingPage/pages/Plans/utils/getCurrencyOfferByPeriod";
import useIsDesktop from "../../../landingPage/hooks/useIsDesktop";
import React, { useRef, useState, useEffect } from "react";
import useOnPaymentButtonClick, {
  getPaymentMethodPricing
} from "../../../landingPage/pages/Plans/utils/useOnPaymentButtonClick";
import useAvailablePaymentMethods from "../../../../../hooks/useAvailablePaymentMethods";
import { SUPPORTED_PAYMENT_METHODS } from "../../PaymentForm/modules/default";
import useSetDefaultPaymentMethod from "../../../../../hooks/useSetDefaultPaymentMethod";
import upgradeIcon from "../../../landingPage/components/LandingTypography/assets/upgrade_icon.svg";
import SelectedPlanDescription from "../../../landingPage/pages/Plans/components/SelectedPlanDescription";
import isSelectedPackageSubscription from "../../../landingPage/pages/Plans/utils/isSelectedPackageSubscription";
import NewModal from "../../../modal/NewModal";
import SelectedPlanName from "../../../landingPage/pages/Plans/components/SelectedPlanName";
import SelectedPlanPrice from "../../../landingPage/pages/Plans/components/SelectedPlanPrice";
import hasSubtractedAmount from "../../../landingPage/pages/Plans/utils/hasSubtractedAmount";
import classNames from "classnames";
import PaymentMethods from "../../../landingPage/pages/Plans/components/PaymentMethods";
import withTranslations from "../../../../../hocs/withTranslations";
import { withRouter } from "react-router-dom";
import withUserData from "../../../../../hocs/withUserData";
import { compose } from "redux";
import withPlans from "../../../../../hocs/withPlans";
import withInvoices from "../../../../../hocs/withInvoices";
import config from "../../../../../config";
import PaymentModal from "../../../landingPage/pages/Plans/components/PaymentModal";
import PaymentButton from "../../../landingPage/pages/Plans/components/PaymentButton";
import InvoiceFrom from "../../../landingPage/pages/Plans/components/InvoiceForms/components/InvoiceForm";
import Checkbox from "../../../../../lib/Checkbox/Checkbox";
import { resolveInvoiceFormDataForPayment } from "../../../../../lib/payment/resolveInvoiceFormDataForPayment";

const NoPermissionPayment = ({
  translate,
  hideModal,
  selectedPlan,
  period,
  paymentDetails,
  paymentFormLoading,
  finalizePayment,
  userData,
  history,
  locale,
  onCancel,
  onClose,
  fetchSavedInvoiceData,
  savedInvoicesEnabled
}) => {
  const currentCurrencyOfferByPeriod = getCurrencyOfferByPeriod({
    currency_offers: selectedPlan?.currency_offers,
    period
  });

  const isDesktop = useIsDesktop();
  const invoiceFromRef = useRef(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState();
  const [showConfirmPaymentModal, setShowConfirmPaymentModal] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const invoicesEnabled = config.invoicesEnabled;
  const { onPaymentButtonClick } = useOnPaymentButtonClick({
    finalizePayment,
    selectedPaymentMethod,
    currentCurrencyOfferByPeriod,
    selectedPlan,
    locale,
    userData,
    history
  });

  const paymentMethodPricing = getPaymentMethodPricing({
    selectedPaymentMethod,
    currentCurrencyOfferByPeriod
  });

  const availablePaymentMethods = useAvailablePaymentMethods({
    currentCurrencyOfferByPeriod,
    supportedPaymentMethods: SUPPORTED_PAYMENT_METHODS
  });

  useSetDefaultPaymentMethod({
    setSelectedPaymentMethod,
    availablePaymentMethods,
    currentCurrencyOfferByPeriod,
    supportedPaymentMethods: SUPPORTED_PAYMENT_METHODS,
    period
  });

  useEffect(() => {
    if (invoicesEnabled) {
      fetchSavedInvoiceData();
    }
  }, [fetchSavedInvoiceData, invoicesEnabled]);

  useEffect(() => {
    if (!invoicesEnabled) {
      return;
    }

    setShowInvoiceForm(savedInvoicesEnabled);
  }, [savedInvoicesEnabled, invoicesEnabled]);

  if (!selectedPlan) {
    return null;
  }

  const title = (
    <>
      <img
        alt={""}
        src={upgradeIcon}
        width={24}
        height={24}
        className={"mr-3"}
      />
      <span>{translate("no-permission-payment_title")}</span>
    </>
  );

  const selectedPlanName = <SelectedPlanName name={selectedPlan.name} />;

  const price = (
    <SelectedPlanPrice
      hasSubtractedAmount={hasSubtractedAmount(paymentMethodPricing)}
      paymentMethodPricing={paymentMethodPricing}
    />
  );
  const description = (
    <SelectedPlanDescription
      currentCurrencyOfferByPeriod={currentCurrencyOfferByPeriod}
      isSelectedPackageSubscription={isSelectedPackageSubscription({
        selectedPaymentMethod
      })}
      paymentDetails={paymentDetails}
      selectedPaymentMethod={selectedPaymentMethod}
      selectedPlan={selectedPlan}
    />
  );

  if (showConfirmPaymentModal) {
    return (
      <>
        <PaymentModal
          selectedPlanName={selectedPlanName}
          price={price}
          description={description}
          selectedPlan={selectedPlan}
          onPaymentButtonClick={async () => {
            const invoiceFormData = await resolveInvoiceFormDataForPayment({
              showInvoiceForm,
              invoiceFormRef: invoiceFromRef
            });

            if (invoiceFormData === false) {
              return;
            }

            onPaymentButtonClick({ invoiceFormData });
          }}
          hideModal={() => {
            onClose?.();
            hideModal?.();
          }}
        />
        <div style={{ display: "none" }}>
          {invoicesEnabled ? (
            <InvoiceFrom
              locale={locale}
              translate={translate}
              ref={invoiceFromRef}
            />
          ) : null}
        </div>
      </>
    );
  }

  return (
    <NewModal
      canHide={true}
      title={title}
      acceptButton={
        <PaymentButton
          className="button-1"
          selectedPlan={selectedPlan}
          onPaymentButtonClick={onPaymentButtonClick}
          userData={userData}
          setShowModal={setShowConfirmPaymentModal}
          selectedPaymentMethod={selectedPaymentMethod}
          paymentFormLoading={paymentFormLoading}
          paymentDetails={paymentDetails}
          currentCurrencyOfferByPeriod={currentCurrencyOfferByPeriod}
          invoiceFromRef={invoiceFromRef}
          showInvoiceForm={showInvoiceForm}
        />
      }
      onClose={onClose}
      cancelButtonText={translate(
        "no-permission-payment-modal_cancel-button-text"
      )}
      onCancel={onCancel}
      hideModal={hideModal}
      hideOnCancel={false}
    >
      <div>
        <div className="p-6 bg-gray rounded mb-6">
          <div className="flex flex-row justify-between">
            <div className="flex flex-row gap-x-10 gap-y-4 mb-4 flex-wrap">
              {selectedPlanName}
              {price}
            </div>
            {invoicesEnabled ? (
              <div className="shrink-0">
                <Checkbox
                  label={translate("plans_payment-bar_invoice")}
                  className={"text-black-8"}
                  checked={showInvoiceForm}
                  onClick={() => {
                    setShowInvoiceForm(prevState => !prevState);
                  }}
                />
              </div>
            ) : null}
          </div>
          <p className="opacity-70 text-xs mb-0">{selectedPlan.description}</p>
          {invoicesEnabled && showInvoiceForm ? (
            <div className={"mt-6 mb-2"}>
              <InvoiceFrom
                locale={locale}
                translate={translate}
                ref={invoiceFromRef}
              />
            </div>
          ) : null}
        </div>

        <div
          className={classNames("flex flex-wrap gap-x-8 gap-y-6", {
            "align-center flex-row": isDesktop,
            "flex-col": !isDesktop
          })}
        >
          <PaymentMethods
            setSelectedPaymentMethod={setSelectedPaymentMethod}
            selectedPaymentMethod={selectedPaymentMethod}
            currentCurrencyOfferByPeriod={currentCurrencyOfferByPeriod}
            availablePaymentMethods={availablePaymentMethods}
          />
          {description}
        </div>
      </div>
    </NewModal>
  );
};

export default compose(
  withTranslations,
  withRouter,
  withUserData,
  withPlans,
  withInvoices
)(NoPermissionPayment);
